import React from "react";
import { useErrorContext, Pane, Text } from "shared";
import { useTranslation } from "react-i18next";
import { Autocomplete } from "@material-ui/lab";
import { CircularProgress, TextField } from "@material-ui/core";
import useTherapist from "../queries/useTherapist";
import { useQueryClient } from "react-query";
import { queryKeys } from "utils/misc";
import { definitions } from "api";
import useChangeTherapistType from "../queries/useChangeTherapistType";

type PT = definitions["therapist_full"];
type TherapistType = "clinical" | "virtual" | "hybrid";
type TTherapistTypes = {
  label: string;
  value: TherapistType;
};

const TherapistTypes: React.VFC = () => {
  const { t } = useTranslation();
  const setError = useErrorContext();
  const { data: pt } = useTherapist();
  const { mutateAsync } = useChangeTherapistType();
  const queryClient = useQueryClient();

  if (!pt) return null;

  const therapistTypes: TTherapistTypes[] = [
    { label: "Clinical", value: "clinical" },
    { label: "Virtual", value: "virtual" },
    { label: "Hybrid", value: "hybrid" },
  ];
  const options = therapistTypes.map(({ value }) => value);

  const change = async (therapist_type: string | null) => {
    if (!therapist_type) return;

    const ptKey = queryKeys.therapist(pt.id);
    const rollbackData = queryClient.getQueryData<PT>(ptKey);
    queryClient.cancelQueries(ptKey);
    queryClient.setQueryData<PT>(ptKey, old => ({ ...old!, therapist_type }));

    try {
      await mutateAsync({ therapist_type });
    } catch (err) {
      queryClient.setQueryData(ptKey, rollbackData);
      setError(t`errors.could_not_update_therapist_type`, err);
    } finally {
      queryClient.invalidateQueries(ptKey);
    }
  };

  const getOption = (option: string) => therapistTypes.find(type => type.value === option);

  return (
    <div>
      <Text paragraph caption secondary>
        {t("form.therapist_type")}
      </Text>
      <Pane dark>
        <Autocomplete
          disableClearable
          size="small"
          ChipProps={{ color: "secondary" }}
          autoHighlight
          value={pt?.therapist_type ?? ""}
          fullWidth
          getOptionLabel={option => `${getOption(option)?.label}`}
          loadingText={<CircularProgress />}
          onChange={(_, therapist_type) => change(therapist_type)}
          options={options}
          renderInput={({ InputProps, inputProps, ...rest }) => (
            <TextField
              variant="standard"
              {...rest}
              InputProps={{ ...InputProps, disableUnderline: true }}
              inputProps={{ ...inputProps, readOnly: true, style: { cursor: "pointer" } }}
            />
          )}
        />
      </Pane>
    </div>
  );
};

export default TherapistTypes;
